import { useState, useEffect, useRef } from 'react';
import { ApplePayButton } from '@tap-payments/apple-pay-button';
import * as Sentry from '@sentry/react';
import { useTapSDK } from '../../hooks/useTapSDK';

interface TapApplePayProps {
    onTokenSuccess: (token: string) => void;
    amount: number;
    currency?: string;
    customerDetails?: any;
}

export const TapApplePay = ({ onTokenSuccess, amount, currency = 'SAR', customerDetails }: TapApplePayProps) => {
    const { publicKey, merchantId } = useTapSDK();
    const [isReady, setIsReady] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [sessionConflict, setSessionConflict] = useState(false);
    const [merchantValidationFailed, setMerchantValidationFailed] = useState(false);
    const buttonKeyRef = useRef(0);

    // التحقق من صحة المبلغ وتنسيقه
    const validatedAmount = Math.max(0.01, Number(amount) || 0.01);
    const formattedAmount = validatedAmount.toFixed(2);

    // إعادة تهيئة الزر عند تغيير المبلغ أو بيانات العميل
    // ⚠️ لا نُعيد البناء إذا كانت هناك جلسة دفع نشطة لتجنب InvalidStateError و 408
    useEffect(() => {
        if (isProcessing) return;
        setIsReady(false);
        setSessionConflict(false);
        buttonKeyRef.current += 1;
    }, [amount, customerDetails?.email, customerDetails?.phone, customerDetails?.name]);

    // عند تعارض الجلسة: ننتظر ثانية ثم نُعيد تهيئة الزر تلقائياً
    useEffect(() => {
        if (!sessionConflict) return;
        const timer = setTimeout(() => {
            setSessionConflict(false);
            setIsReady(false);
            buttonKeyRef.current += 1;
            setIsProcessing(false);
        }, 1200);
        return () => clearTimeout(timer);
    }, [sessionConflict]);

    // آلية احتياطية: إذا لم يتم استدعاء onReady خلال 3 ثوانٍ، نعتبر الزر جاهزاً
    useEffect(() => {
        if (!isReady) {
            const timeout = setTimeout(() => {
                    if (!isReady) {
                        setIsReady(true);
                    }
            }, 3000);
            return () => clearTimeout(timeout);
        }
    }, [isReady]);

    // اصطياد Promise المرفوضة غير المُعالجة من Tap SDK داخلياً
    // (InvalidAccessError / InvalidStateError عبر onunhandledrejection)
    useEffect(() => {
        const handler = (event: PromiseRejectionEvent) => {
            const reason = event.reason;
            const msg: string =
                reason?.message || reason?.name || String(reason || '');

            const isApplePaySDKError =
                msg.includes('InvalidAccessError') ||
                msg.includes('InvalidStateError') ||
                msg.includes('InvalidAbsoluteError') ||
                msg.includes('does not support the operation') ||
                msg.includes('active payment session') ||
                msg.includes('completePayment') ||
                msg.includes('completeShippingMethodSelection') ||
                msg.includes('completeShippingContactSelection') ||
                msg.includes('validatemerchant') ||
                msg.includes('onvalidatemerchant') ||
                msg.includes('417') ||
                msg.includes('ApplePay') ||
                msg.includes('apple_pay') ||
                msg.includes('tap.company') ||
                msg.includes('mw-sdk');

            if (isApplePaySDKError) {
                event.preventDefault(); // يمنع Sentry من التقاطه كـ unhandled
                // إذا كان خطأ تعارض جلسة، نُعيد التهيئة هادئاً
                if (
                    msg.includes('active payment session') ||
                    msg.includes('InvalidStateError') ||
                    msg.includes('InvalidAbsoluteError')
                ) {
                    setSessionConflict(true);
                }
            }
        };

        window.addEventListener('unhandledrejection', handler);
        return () => window.removeEventListener('unhandledrejection', handler);
    }, []);

    if (!publicKey || !merchantId) return null;

    // التحقق من أن المبلغ صحيح قبل عرض الزر
    if (validatedAmount < 0.01) {
        return (
            <div style={{ padding: '12px', textAlign: 'center', color: 'oklch(0.61 0.25 28.5)', fontSize: '14px' }}>
                المبلغ غير صحيح. يرجى التحقق من قيمة الطلب.
            </div>
        );
    }

    // فشل التحقق من التاجر — الدومين غير مسجّل في Apple Pay
    if (merchantValidationFailed) {
        return (
            <div style={{
                padding: '14px 16px',
                borderRadius: '8px',
                background: '#fff3f3',
                border: '1px solid #ffcccc',
                color: '#c0392b',
                fontSize: '14px',
                textAlign: 'center',
                lineHeight: '1.6'
            }}>
                Apple Pay غير متاح حالياً. يرجى اختيار طريقة دفع أخرى.
            </div>
        );
    }

    // عرض رسالة مؤقتة عند تعارض جلسة Apple Pay
    if (sessionConflict) {
        return (
            <div style={{ padding: '14px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                <div className="tap-spinner" aria-hidden="true" style={{ margin: '0 auto 8px' }}></div>
                جاري إعادة تحميل Apple Pay…
            </div>
        );
    }

    return (
        <div className="tap-card-wrapper" style={{ width: '100%', marginTop: '10px' }}>
            <div className={`tap-sdk-mount ${isReady ? 'is-ready' : 'is-loading'}`} style={{ minHeight: '120px' }}>
                {!isReady && (
                    <div className="tap-sdk-loading" role="status" aria-live="polite" aria-label="جاري تحميل Apple Pay">
                        <div className="tap-spinner" aria-hidden="true"></div>
                        <div className="tap-loading-text">جاري تحميل نموذج الدفع…</div>
                    </div>
                )}
                <div style={{ 
                    visibility: isReady ? 'visible' : 'hidden',
                    opacity: isReady ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                    // تعطيل التفاعل أثناء معالجة الدفع لمنع إنشاء جلسة ثانية (Bug #3)
                    pointerEvents: isProcessing ? 'none' : 'auto',
                    cursor: isProcessing ? 'not-allowed' : 'default',
                }}>
                <ApplePayButton
                    key={buttonKeyRef.current}
                    debug={false}
                    scope='TapToken'
                    publicKey={publicKey}
                    environment={publicKey.startsWith('pk_live') ? 'production' : 'development'}
                    merchant={{
                        domain: window.location.hostname,
                        id: merchantId
                    }}
                    acceptance={{
                        // Leave empty to auto-detect supported brands from your Tap account
                        // This prevents "supported networks list is not valid" error
                    }}
                    features={{
                        supportsCouponCode: false
                    }}
                    transaction={{
                        currency: currency,
                        amount: formattedAmount
                    }}
                    customer={{
                        name: [
                            {
                                lang: 'en',
                                first: customerDetails?.name?.split(' ')[0] || 'Guest',
                                last: customerDetails?.name?.split(' ').slice(1).join(' ') || 'User',
                                middle: ''
                            }
                        ],
                        contact: {
                            email: customerDetails?.email || 'guest@example.com',
                            phone: {
                                number: customerDetails?.phone || '500000000',
                                countryCode: '+966'
                            }
                        }
                    }}
                    interface={{
                        locale: 'ar',
                        theme: 'dark',
                        type: 'buy',
                        edges: 'curved'
                    }}
                    onReady={() => {
                        setIsReady(true);
                    }}
                    onSuccess={async (data: any) => {
                        if (isProcessing) return; // منع المعالجة المتكررة
                        setIsProcessing(true);
                        
                        if (data.id) {
                            try {
                                await onTokenSuccess(data.id);
                            } catch (error) {
                                // Token processing failed
                            } finally {
                                setIsProcessing(false);
                            }
                        } else {
                            setIsProcessing(false);
                        }
                    }}
                    onError={(error: any) => {
                        setIsProcessing(false);

                        const errorStr = JSON.stringify(error);
                        const errorMsg: string = error?.message || error?.[0]?.message || errorStr;

                        // أخطاء حالة الجلسة — طبيعية تماماً، لا تُرسل إلى Sentry
                        // InvalidAbsoluteError / InvalidStateError / 408 active session
                        const isSessionStateError =
                            error?.code === 400 ||
                            error?.[0]?.code === 400 ||
                            error?.code === 408 ||
                            errorStr.includes('active payment session') ||
                            errorStr.includes('InvalidAbsoluteError') ||
                            errorStr.includes('InvalidStateError') ||
                            errorMsg.includes('InvalidAbsoluteError') ||
                            errorMsg.includes('InvalidStateError') ||
                            errorMsg.includes('does not support the operation');

                        if (isSessionStateError) {
                            setSessionConflict(true);
                            return;
                        }

                        // فشل التحقق من التاجر (417) — الدومين غير مسجّل في Apple Pay
                        const isMerchantValidationError =
                            errorStr.includes('417') ||
                            errorStr.includes('validatemerchant') ||
                            errorStr.includes('onvalidatemerchant') ||
                            error?.message?.includes('417');

                        if (isMerchantValidationError) {
                            setMerchantValidationFailed(true);
                            Sentry.withScope((scope) => {
                                scope.setLevel('fatal');
                                scope.setTag('source', 'apple_pay_merchant_validation');
                                scope.setTag('gateway', 'apple_pay');
                                scope.setExtra('raw_error', error);
                                if (customerDetails?.email) {
                                    scope.setUser({ email: customerDetails.email, username: customerDetails.name || '' });
                                }
                                Sentry.captureMessage('Apple Pay: Merchant validation failed (417) — domain not registered');
                            });
                            return;
                        }

                        // FrameError — خطأ داخلي في iframe عند إلغاء التحميل، حالة طبيعية
                        const isFrameError =
                            errorStr.includes('FrameError') ||
                            errorStr.includes('frame') ||
                            errorMsg.includes('FrameError');

                        if (isFrameError) {
                            // إعادة تهيئة هادئة دون إرسال إلى Sentry
                            setIsReady(false);
                            buttonKeyRef.current += 1;
                            return;
                        }

                        // باقي الأخطاء الحقيقية → Sentry
                        Sentry.withScope((scope) => {
                            scope.setLevel('error');
                            scope.setTag('source', 'tap_iframe_load');
                            scope.setTag('gateway', 'apple_pay');
                            scope.setExtra('raw_error', error);
                            if (customerDetails?.email) {
                                scope.setUser({ email: customerDetails.email, username: customerDetails.name || '' });
                            }
                            Sentry.captureMessage(`Apple Pay iframe onError: ${error?.message || JSON.stringify(error)}`);
                        });
                    }}
                    onCancel={() => {
                        setIsProcessing(false);
                    }}
                />
                </div>
            </div>
        </div>
    );
};
